/**
 * useCompositeDataOrchestrator Hook
 *
 * Fully encapsulated orchestrator for CompositeDataPicker integration.
 * Replaces useDataReferenceOrchestrator with the new data-pickers module.
 *
 * Key advantages over useDataReferenceOrchestrator:
 * - NO double data loading (data loaded once for both UI and AI)
 * - Autonomous data loading (CompositeDataPicker loads its own data)
 * - Better granularity (supports individual array elements)
 * - Modern path format: @audience[id].actors[actorId].field[index]
 *
 * This hook provides:
 * - Ready-to-use props for CompositeDataPicker (one-liner in JSX)
 * - Automatic data conversion (Firestore format from paths)
 * - Hydrated data from CompositeDataPicker (no separate Hydrator call!)
 * - Loading states from underlying AI services
 * - Direct access to AI services (for webhook calls)
 *
 * Related: Migration from DataReferenceTreeWrapper to CompositeDataPicker
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { useStrategyAIOrchestration, type UseStrategyAIOrchestratorResult } from './useStrategyAIOrchestration';
import {
  pathsToFirestoreFormat,
  pathsToAiSourceDataIds,
  type FirestoreSourceData,
} from '@modules/data-pickers/utils/pathsToFirestoreFormat';
import type {
  CompositeDataPickerProps,
  HydratedData,
  CompositeLoadingState,
} from '@modules/data-pickers/components/CompositeDataPicker/types';

/**
 * Hook options
 */
export interface UseCompositeDataOrchestratorOptions {
  /** Project ID for data loading */
  projectId: string;

  /** Strategy service instance (created by parent) */
  strategyService: StrategyService;

  /** Initial selection paths (e.g., from formData.selectedSourceDataPaths) */
  initialSelection?: string[];

  /**
   * Additional paths to load for validation (e.g., from dataMapping)
   *
   * These paths will be loaded but NOT added to the selection state.
   * Useful for DataMappingViewer to validate AI-generated paths against real data.
   */
  dataMappingPaths?: string[];

  /**
   * Callback when selection changes
   *
   * Receives pre-converted data in all needed formats:
   * - selectedIds: Original selection paths (for state)
   */
  onSelectionChange?: (data: {
    selectedIds: string[];
  }) => void;

  /** Dialog open state (triggers webhook settings loading) */
  open: boolean;

  /** Auto-select all items when no saved selection exists (use only in create mode) */
  selectAll?: boolean;
}

/**
 * AI source data IDs structure (for backward compatibility)
 */
export interface SourceDataIds {
  audienceIds: string[];
  taskIds: string[];
  studyCaseIds: string[];
  reviewIds: string[];
  ratingIds: string[];
  bigNumberIds: string[];
  insightIds: string[];
}

/**
 * Hook result
 */
export interface UseCompositeDataOrchestratorResult {
  // ================================
  // SIMPLE API (most common use case)
  // ================================

  /**
   * Ready-to-use props for CompositeDataPicker
   *
   * Use as: <CompositeDataPicker {...orchestrator.compositeProps} />
   */
  compositeProps: CompositeDataPickerProps;

  /**
   * Converted source data in Firestore format
   *
   * Use as: await service.create({ sourceData: orchestrator.firestoreData })
   *
   * Format: { audiences: [...], tasks: [...] }
   */
  firestoreData: FirestoreSourceData;

  /**
   * Extracted ID arrays for AI webhook
   *
   * Format: { audienceIds: [...], taskIds: [...], ... }
   */
  aiSourceDataIds: SourceDataIds;

  /**
   * Hydrated data from CompositeDataPicker
   *
   * Use as payload for AI webhook - NO need to call SourceDataHydrator!
   *
   * Format: { audiences: [...with actors], tasks: [...] }
   */
  hydratedData: HydratedData;

  /**
   * Current selection paths
   *
   * Use for DataMappingViewer validation - check if AI paths exist in selection
   */
  currentSelection: string[];

  // ================================
  // LOADING STATES
  // ================================

  /**
   * Overall loading state from CompositeDataPicker
   */
  loading: boolean;

  /**
   * Detailed loading state from CompositeDataPicker
   */
  loadingState: CompositeLoadingState | null;

  // ================================
  // ADVANCED API (for AI operations)
  // ================================

  /**
   * Direct access to AI orchestration services
   *
   * Use for:
   * - orchestrator.services.aiOrchestration.strategyOrchestrator.createWithAIServer()
   * - orchestrator.services.aiOrchestration.webhookSettings
   * - orchestrator.services.aiOrchestration.project
   */
  services: {
    aiOrchestration: UseStrategyAIOrchestratorResult;
  };
}

/**
 * useCompositeDataOrchestrator Hook
 *
 * Provides complete encapsulation of CompositeDataPicker integration.
 * Replaces useDataReferenceOrchestrator with modern data-pickers module.
 *
 * Usage Example (StrategyFormDialog):
 * ```typescript
 * const orchestrator = useCompositeDataOrchestrator({
 *   projectId,
 *   strategyService,
 *   initialSelection: formData.selectedSourceDataPaths,
 *   onSelectionChange: (data) => {
 *     setFormData(prev => ({
 *       ...prev,
 *       selectedSourceDataPaths: data.selectedIds,
 *     }));
 *     updateCurrent(newData);
 *   },
 *   open,
 * });
 *
 * // In JSX - 1 line instead of DataReferenceTreeWrapper with 20 props:
 * <CompositeDataPicker {...orchestrator.compositeProps} />
 *
 * // In handleSubmit - all data ready:
 * await strategyService.create({
 *   sourceData: orchestrator.firestoreData,  // For DB
 * });
 *
 * // For AI - pass orchestrator.aiSourceDataIds into StrategyOrchestrator server methods.
 * ```
 */
export function useCompositeDataOrchestrator(
  options: UseCompositeDataOrchestratorOptions
): UseCompositeDataOrchestratorResult {
  const {
    projectId,
    strategyService,
    initialSelection = [],
    dataMappingPaths = [],
    onSelectionChange,
    open,
    selectAll = false,
  } = options;

  // ================================
  // INTERNAL STATE
  // ================================

  // Auto-populate selection from dataMapping if user hasn't selected anything
  // This shows user what data AI used to create the block
  // Filter out non-entity paths (self.dataUsage..., @variable[...]) that can't be resolved to tree nodes
  const effectiveInitialSelection = useMemo(() => {
    if (initialSelection.length === 0 && dataMappingPaths.length > 0) {
      const entityPaths = dataMappingPaths.filter((p: string) =>
        p.startsWith('@audience[') || p.startsWith('@task[') || p.startsWith('@rating[') ||
        p.startsWith('@review[') || p.startsWith('@studyCase[') || p.startsWith('@bigNumber[')
      );
      console.log('[useCompositeDataOrchestrator] Auto-populating from dataMapping:', {
        total: dataMappingPaths.length,
        entityPaths: entityPaths.length,
        filtered: dataMappingPaths.length - entityPaths.length,
        paths: entityPaths,
      });
      return entityPaths;
    }
    return initialSelection;
  }, [initialSelection, dataMappingPaths]);

  // Current selection paths
  const [currentSelection, setCurrentSelection] = useState<string[]>(effectiveInitialSelection);

  // Hydrated data from CompositeDataPicker (provided via callback)
  const [hydratedData, setHydratedData] = useState<HydratedData>({
    audiences: [],
    tasks: [],
  });

  // Loading state from CompositeDataPicker
  const [loadingState, setLoadingState] = useState<CompositeLoadingState | null>(null);

  // Track if we've initialized to prevent loops
  const isInitializedRef = useRef(false);

  // Sync with effectiveInitialSelection when it changes
  useEffect(() => {
    const selectionChanged = JSON.stringify(effectiveInitialSelection) !== JSON.stringify(currentSelection);

    if (selectionChanged && !isInitializedRef.current) {
      console.log('[useCompositeDataOrchestrator] Syncing with effectiveInitialSelection:', {
        mode: effectiveInitialSelection.length === 0 ? 'create (reset)' : 'edit/update',
        from: currentSelection.length,
        to: effectiveInitialSelection.length,
        source: initialSelection.length === 0 ? 'dataMapping' : 'initialSelection',
      });
      setCurrentSelection(effectiveInitialSelection);
    }

    if (!isInitializedRef.current && open) {
      isInitializedRef.current = true;
    }
  }, [effectiveInitialSelection, currentSelection, open, initialSelection.length]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      isInitializedRef.current = false;
    }
  }, [open]);

  // ================================
  // AI SERVICES
  // ================================

  // AI orchestration hook (provides webhook settings, AI services, project data)
  const aiOrchestration = useStrategyAIOrchestration({
    projectId,
    strategyService,
    open,
  });

  // ================================
  // DATA CONVERSION (memoized)
  // ================================

  // Convert current selection to Firestore format
  const firestoreData = useMemo<FirestoreSourceData>(() => {
    if (currentSelection.length === 0) {
      return {
        audiences: [],
        tasks: [],
        variableIds: [],
        insightVariableIds: [],
        studyCases: [],
        reviews: [],
        ratings: [],
        bigNumbers: [],
        insightIds: [],
        // W13 (IS1): the thesis/category planes of the insight picker.
        insightThesisIds: [],
        insightCategories: [],
      };
    }

    return pathsToFirestoreFormat(currentSelection);
  }, [currentSelection]);

  // Extract AI source data IDs
  const aiSourceDataIds = useMemo<SourceDataIds>(() => {
    return pathsToAiSourceDataIds(currentSelection);
  }, [currentSelection]);

  // ================================
  // CALLBACKS FOR COMPOSITE DATA PICKER
  // ================================

  /**
   * Handle selection change from CompositeDataPicker
   */
  const handleSelectionChange = useCallback(
    (newSelection: string[]) => {
      // Update internal state
      setCurrentSelection(newSelection);

      // Notify parent
      onSelectionChange?.({
        selectedIds: newSelection,
      });
    },
    [onSelectionChange]
  );

  /**
   * Handle hydrated data change from CompositeDataPicker
   *
   * This is the key difference from useDataReferenceOrchestrator:
   * - Old: Call SourceDataHydrator.hydrate() separately (double loading!)
   * - New: Hydrated data provided directly via callback (single loading!)
   */
  const handleHydratedDataChange = useCallback((data: HydratedData) => {
    console.log('[useCompositeDataOrchestrator] Hydrated data updated:', {
      audiences: data.audiences.length,
      tasks: data.tasks.length,
    });
    setHydratedData(data);
  }, []);

  /**
   * Handle loading state change from CompositeDataPicker
   */
  const handleLoadingChange = useCallback((state: CompositeLoadingState) => {
    setLoadingState(state);
  }, []);

  // ================================
  // COMPOSITE PICKER PROPS (memoized)
  // ================================

  /**
   * Build complete props object for CompositeDataPicker
   */
  const compositeProps = useMemo<CompositeDataPickerProps>(() => {
    return {
      projectId,
      savedSelections: currentSelection,
      onSelectionChange: handleSelectionChange,
      onHydratedDataChange: handleHydratedDataChange,
      onLoadingChange: handleLoadingChange,
      pickers: [
        { type: 'audience', label: 'Audiences', enabled: true, selectAll },
        { type: 'task', label: 'Tasks', enabled: true, selectAll },
        { type: 'socialproof', label: 'Social Proof', enabled: true, selectAll },
        { type: 'insights', label: 'Insights', enabled: true, selectAll },
      ],
      layout: 'tabs',
      height: 400,
      showInvalidPaths: false, // Invalid paths shown in AI Data Usage section instead
    };
  }, [
    projectId,
    currentSelection,
    handleSelectionChange,
    handleHydratedDataChange,
    handleLoadingChange,
  ]);

  // ================================
  // LOADING STATE
  // ================================

  // Aggregate loading state
  // If hydratedData has content, we're not loading anymore (data is ready)
  const hasHydratedData = hydratedData.audiences.length > 0 || hydratedData.tasks.length > 0 ||
    (hydratedData.reviews?.length ?? 0) > 0 || (hydratedData.ratings?.length ?? 0) > 0 ||
    (hydratedData.studyCases?.length ?? 0) > 0 || (hydratedData.bigNumbers?.length ?? 0) > 0 ||
    (hydratedData.insights?.analyses?.length ?? 0) > 0;

  const loading = useMemo(() => {
    // If we have hydrated data, loading is complete
    if (hasHydratedData) {
      return false;
    }

    return (
      (loadingState?.isLoading ?? true) ||
      aiOrchestration.webhookSettingsLoading ||
      aiOrchestration.projectLoading
    );
  }, [
    hasHydratedData,
    loadingState?.isLoading,
    aiOrchestration.webhookSettingsLoading,
    aiOrchestration.projectLoading,
  ]);

  // ================================
  // RETURN RESULT
  // ================================

  return {
    // Simple API
    compositeProps,
    firestoreData,
    aiSourceDataIds,
    hydratedData,
    currentSelection,

    // Loading states
    loading,
    loadingState,

    // Advanced API
    services: {
      aiOrchestration,
    },
  };
}
